/* @flow */

import * as React from 'react';
import { withProps } from 'recompose';
import colorUtils from 'color';
import { find, memoize } from 'lodash/fp';
import styled from '@emotion/styled';
import { css } from 'glamor';
import { borderColor, borderWidth, padding, position } from 'polished';
import Icon from 'app/components/commons/Icon';
import Tappable from 'app/components/commons/Tappable';
import { hover } from 'app/utils/cssUtils';

type Props = {
  className?: string,
  disabled?: boolean,
  height?: number,
  loading?: boolean,
  onChange: string | (number => void),
  options: Array<{ label: string, value?: string | number }>,
  placeholder?: string,
  value?: string | number
};

type State = {
  isOpen: boolean
};

const Wrapper = styled.div(({ theme }) => ({
  color: theme.color.panelAccent,
  position: 'relative'
}));

const Value = styled.div(({ theme, disabled, height }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: disabled
    ? colorUtils(theme.color.basis4)
        .alpha(0.5)
        .string()
    : theme.color.panel,
  borderColor: disabled
    ? colorUtils(theme.color.basis4)
        .alpha(0)
        .string()
    : theme.color.basis5,
  borderStyle: 'solid',
  borderWidth: 1,
  ...padding(5, 10),
  height: height || 'inherit'
}));

const Item = styled.div(({ theme }) => ({
  backgroundColor: theme.color.panel,
  borderColor: 'transparent',
  borderStyle: 'solid',
  borderWidth: 1,
  ...padding(5, 10),
  ...hover({
    backgroundColor: theme.color.primary,
    color: theme.color.white
  })
}));

const List = styled.div(({ theme }) => ({
  ...borderColor(theme.color.panelAccent, theme.color.basis5),
  borderStyle: 'solid',
  ...borderWidth(0, 1, 3),
  maxHeight: 272,
  overflow: 'auto',
  ...position('absolute', '100%', 0, null),
  zIndex: theme.zIndex.menu
}));

const Placeholder = styled.span({
  textTransform: 'uppercase'
});

const Arrow = styled(({ className, open }) => (
  <span className={`${className} ${open ? 'ion-ios-arrow-up' : 'ion-ios-arrow-down'}`} />
))(({ top }) => position('absolute', top, 10, null, null));

const Load = styled(({ className }) => <div className={`${className} ion-load-c`} />)({
  animationDuration: '0.5s',
  animationIterationCount: 'infinite',
  animationName: css.keyframes({
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' }
  }),
  animationTimingFunction: 'linear',
  ...position('absolute', 5, 10, null, null)
});

const StyledIcon = styled(Icon)(({ theme }) => ({
  marginRight: theme.spacing.xsmall
}));

class FlexiSelect extends React.PureComponent<Props, State> {
  props: Props;

  state: State = {
    isOpen: false
  };

  ref: React.ElementRef<HTMLElement> = React.createRef(); // eslint-disable-line react/sort-comp

  static defaultProps = {
    className: undefined,
    disabled: false,
    height: undefined,
    loading: false,
    placeholder: 'Select...',
    value: undefined
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    return {
      isOpen: !props.loading && state.isOpen
    };
  }

  componentDidMount() {
    document.body.addEventListener('click', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.handleClickOutside);
  }

  handleClickOutside = event => {
    const { current } = this.ref;

    if (!current.contains(event.target)) {
      this.setState({
        isOpen: false
      });
    }
  };

  handleSelect = memoize((val: string | number) => () => {
    const { onChange, value } = this.props;

    if (val === value) {
      this.setState({
        isOpen: false
      });
    } else {
      onChange(val);
    }
  });

  toggleOpen = () => {
    this.setState(state => ({
      isOpen: !state.isOpen
    }));
  };

  render() {
    const { className, disabled, height, loading, options, placeholder, value } = this.props;
    const { isOpen } = this.state;
    const selected = find({ value }, options);

    if (loading) {
      return (
        <Wrapper className={className} ref={this.ref}>
          <Value height={height}>Loading...</Value>
          <Load />
        </Wrapper>
      );
    }

    if (options.length === 0) {
      return (
        <Wrapper className={className} ref={this.ref}>
          <Value height={height}>
            <Placeholder>No available options</Placeholder>
          </Value>
        </Wrapper>
      );
    }

    return (
      <Wrapper className={className} ref={this.ref}>
        <Tappable component={Value} onTap={this.toggleOpen} disabled={disabled} height={height}>
          {selected ? (
            <React.Fragment>
              {selected.icon && <StyledIcon type={selected.icon} />}
              <span>{selected.label}</span>
            </React.Fragment>
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )}
        </Tappable>
        {isOpen && !disabled && (
          <List>
            {options.map((opt, i) => (
              <Tappable
                key={i} // eslint-disable-line react/no-array-index-key
                component={Item}
                onTap={this.handleSelect(opt.value)}
              >
                {opt.icon && <StyledIcon type={opt.icon} />}
                {opt.label}
              </Tappable>
            ))}
          </List>
        )}
        <Arrow open={isOpen} top={height ? parseInt(height / 4, 10) : 5} />
      </Wrapper>
    );
  }
}

const remountOnValueChange = withProps(({ value }) => ({
  key: value
}));

export default remountOnValueChange(FlexiSelect);
